import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import {
  taConsignorMappingService,
  TAConsignorMappingDto,
  BulkUpdateTAConsignorMappingRequest
} from '../../../../../services/taConsignorMappingService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { TAConsignorMappingSidePanel } from './TAConsignorMappingSidePanel';
import { TAConsignorMappingCreateModal } from './TAConsignorMappingCreateModal';

interface TAConsignorMappingDataTableProps {
  refreshTrigger?: number;
}

export const TAConsignorMappingDataTable: React.FC<TAConsignorMappingDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  const [data, setData] = useState<TAConsignorMappingDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const inlineEditConfig = useInlineEdit<TAConsignorMappingDto>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, editedData) => {
      const request: BulkUpdateTAConsignorMappingRequest = {
        companyNo: originalRow.companyNo,
        consignorCode: (editedData.consignorCode as string) ?? originalRow.consignorCode,
        externalConsignorId: (editedData.externalConsignorId as string) ?? originalRow.externalConsignorId,
        isDefault: (editedData.isDefault as boolean) ?? originalRow.isDefault,
        description: (editedData.description as string) ?? originalRow.description
      };
      await taConsignorMappingService.bulkUpdate([request]);
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminDelivery.common.errors.saveFailed', {}, 'Feil ved lagring'))
  });

  useEffect(() => { loadData(); }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) loadData();
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;
    setIsLoading(true);
    setError(null);
    try {
      const mappings = await taConsignorMappingService.getCurrent().catch(() =>
        taConsignorMappingService.getAll().catch(() => [])
      );
      setData(mappings);
    } catch (err: any) {
      setError(t('manager.modules.adminDelivery.consignor.errors.loadFailed', {}, 'Kunne ikke laste avsendermappinger. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  const columnFactory = createColumnFactory<TAConsignorMappingDto>();

  const columns: ColumnDefinition<TAConsignorMappingDto>[] = useMemo(() => [
    columnFactory.createTextColumn('consignorCode', 'consignorCode', t('manager.modules.adminDelivery.consignor.fields.consignorCode', {}, 'Avsenderkode'), {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      editable: true
    }),
    columnFactory.createTextColumn('externalConsignorId', 'externalConsignorId', t('manager.modules.adminDelivery.consignor.fields.externalConsignorId', {}, 'Ekstern avsender-ID'), {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),
    columnFactory.createBooleanColumn('isDefault', 'isDefault', t('manager.modules.adminDelivery.consignor.columns.isDefault', {}, 'Standard'), {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true
    }),
    columnFactory.createTextColumn('description', 'description', t('common.fields.description', {}, 'Beskrivelse'), {
      minWidth: 250,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [t]);

  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminDelivery.consignor.errors.selectAtLeastOne', {}, 'Velg minst én avsendermapping for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  const tableActions: TableAction<TAConsignorMappingDto>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminDelivery.consignor.create.title', {}, 'Ny avsendermapping'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminDelivery.common.maintain', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'ta-consignor-mapping-table',
            allowRowSelection: { mode: 'multi', selectAllMode: 'all-data' }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {showBulkEditModal && selectedIds.length > 0 && (
        <TAConsignorMappingSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => { loadData(); setSelectedIds([]); setShowBulkEditModal(false); }}
        />
      )}

      {showCreateModal && (
        <TAConsignorMappingCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { loadData(); setShowCreateModal(false); }}
        />
      )}
    </div>
  );
};
